// Host-modules registry exposed to dynamically-loaded plugins.
//
// This is the magnis-side equivalent of VSCode's `vscode` API module:
// the single, curated surface plugin TSX is allowed to import from at
// runtime. Adding an export here = expanding the public API contract.
// Removing one = breaking change for plugins.
//
// At app bootstrap, `installHostModules()` writes this registry to
// `window.__magnis_host`. The backend's `/api/plugins/__host-shim.js`
// endpoint serves tiny ESM modules that read named bindings from that
// global. The plugin sees `import { InfoCard } from "@magnis/host/ui"`
// and gets the *same instance* of `InfoCard` the host uses.
//
// Categories mirror VSCode-style API areas. Each category is a flat
// re-export bag — no nested namespaces. Each named export here MUST be
// listed in `backend/src/services/plugin_ui/host_shim.rs` so the
// generated shim contains the matching `export const`.

import * as React from "react";
import * as ReactDom from "react-dom";
import * as ReactJsxRuntime from "react/jsx-runtime";
import * as ReactQuery from "@tanstack/react-query";
// Extra third-party UI libs plugins opt into via manifest.ui.extra_bare_imports
// (telegram's virtualized chat list + zustand store).
import { Virtuoso } from "react-virtuoso";
import { useStore } from "zustand";
import { createStore } from "zustand/vanilla";

import * as ui from "@/components/ui";
import { useAppRuntime } from "@/providers/AppRuntimeProvider";
import { defineModule } from "@/modules/_base/defineModule";
import { BaseEntityCard } from "@/modules/_base/BaseEntityCard";
import { BaseToolCallCard } from "@/modules/_base/BaseToolCallCard";
import { ActionPrefix } from "@/modules/_base/ActionPrefix";
import { registerSchemaVisuals } from "@/modules/_base/schemaIcons";
import { EntityCardRenderer } from "@/runtime/EntityCardRenderer";
import { ListPaneHeaderActions } from "@/modules/_base/ListPaneHeaderActions";
import { useEntityFacet } from "@/modules/_base/useEntityFacet";
import { EntityDetailTabs } from "@/modules/shared/EntityDetailTabs";
import { MarkdownEditor } from "@/components/shared/MarkdownEditor";
import { useEditorMentionSuggestion } from "@/components/shared/markdown/useEditorMentionSuggestion";
import { toAvatarColor, pickAvatarColor } from "@/utils/avatar";
import { decodeHtmlEntities, initialsFromName } from "@/utils/text";
import { formatEmailDate, formatTimeAgo, formatMessageTime } from "@/utils/time";
import { formatFileSize } from "@/utils/fileSize";
import { mimeToIcon } from "@/utils/mime";
import { renderableMediaUrl } from "@/utils/mediaUrl";
import { ExpansionContext } from "@/components/agent/expansionContext";
import { ExpandableEntityCard } from "@/components/agent/ExpandableEntityCard";
import { AllowlistDropdown, extractEntities } from "@/components/agent/AgentPanelBlocks";
import { MarkdownText } from "@/components/agent/MarkdownText";
import { setupEventInvalidation } from "@/runtime/transport/queryInvalidation";
import { useRouterContext } from "@/providers/RouterProvider";
import { useModuleList } from "@/hooks/useModuleList";
import { ModuleLayout } from "@/layout/ModuleLayout";
import { DetailPane } from "@/components/layout";
import { PaneFooterBar, PaneFrame, PaneHeaderBar, PaneContent } from "@/components/layout/PaneBlocks";
import { MessageComposer } from "@/components/shared/MessageComposer";
import { useComposerDraft, writeDraftDirect } from "@/components/shared/composer/useComposerDraft";
import { useComposerMountRegistry } from "@/components/shared/composer/ComposerMountContext";
import { applyComposerEvent } from "@/components/shared/composer/composerApplyHandler";
import { uploadBrowserFile, uploadFile } from "@/hooks/fileUpload";
import { authHeaders } from "@/store/authStore";

// Keys are URL-safe slugs matched by `?m=<slug>` in the backend
// host-shim. Slug list lives in `backend/.../transpile.rs::BareImportMap`
// and `backend/.../host_shim.rs::shim_exports`. Drift = runtime
// `undefined` on the plugin side → loud crash.
const HOST_MODULES = {
  "react": React,
  "react-dom": ReactDom,
  "react-jsx-runtime": ReactJsxRuntime,
  "react-query": ReactQuery,
  "react-virtuoso": { Virtuoso },
  "zustand": { useStore },
  "zustand/vanilla": { createStore },

  "ui": { ...ui },

  "runtime": {
    useAppRuntime,
    setupEventInvalidation,
    useRouterContext,
    uploadBrowserFile,
    uploadFile,
    authHeaders,
    useModuleList,
    EntityCardRenderer,
  },

  "layout": {
    DetailPane,
    PaneFooterBar,
    PaneFrame,
    PaneHeaderBar,
    PaneContent,
    ModuleLayout,
  },

  "composer": {
    MessageComposer,
    useComposerDraft,
    writeDraftDirect,
    useComposerMountRegistry,
    applyComposerEvent,
  },

  "base": {
    defineModule,
    BaseEntityCard,
    BaseToolCallCard,
    ActionPrefix,
    ListPaneHeaderActions,
    useEntityFacet,
    EntityDetailTabs,
    registerSchemaVisuals,
  },

  "markdown": {
    MarkdownEditor,
    useEditorMentionSuggestion,
  },

  "utils": {
    toAvatarColor,
    pickAvatarColor,
    decodeHtmlEntities,
    initialsFromName,
    formatEmailDate,
    formatTimeAgo,
    formatMessageTime,
    formatFileSize,
    mimeToIcon,
    renderableMediaUrl,
  },

  "agent": {
    ExpansionContext,
    ExpandableEntityCard,
    AllowlistDropdown,
    MarkdownText,
    extractEntities,
  },
} as const;

declare global {
  interface Window {
    __magnis_host?: typeof HOST_MODULES;
  }
}

export function installHostModules(): void {
  window.__magnis_host = HOST_MODULES;
}

export type HostModules = typeof HOST_MODULES;
